import { z } from 'zod';
import { optionalEmailInput, taxNumberInput } from './customers';
import { isValidIban } from './identity';

/**
 * Suppliers: who the agency buys a service from. A supplier provides some kinds
 * of service, and only those can be bought from it. What a service costs is owed
 * to its supplier, in the service's currency.
 */

export const SUPPLIER_KINDS = ['flight', 'hotel', 'car', 'extra', 'visa'] as const;
export type SupplierKind = (typeof SUPPLIER_KINDS)[number];
export const SUPPLIER_KIND_LABELS: Record<SupplierKind, string> = {
  flight: 'Uçak bileti',
  hotel: 'Otel',
  car: 'Araç kiralama',
  extra: 'Ek hizmet',
  visa: 'Vize',
};

const version = z.number().int().positive();
const blankToNull = <T>(v: T) => (v === '' ? null : v);
const optional = (max: number) =>
  z.string().trim().max(max).nullable().optional().transform(blankToNull);

const iban = z
  .string()
  .transform((v) => v.replace(/\s/g, '').toUpperCase())
  .refine((v) => v === '' || isValidIban(v), 'IBAN doğrulanamadı; harf ve rakamları kontrol edin')
  .nullable()
  .optional()
  .transform(blankToNull);

const supplierFields = {
  name: z.string().trim().min(1, 'Tedarikçi adını girin').max(120),
  kinds: z
    .array(z.enum(SUPPLIER_KINDS))
    .min(1, 'Tedarikçinin verdiği en az bir hizmeti seçin')
    .transform((kinds) => SUPPLIER_KINDS.filter((k) => kinds.includes(k))),
  contactName: optional(120),
  phone: optional(40),
  email: optionalEmailInput,
  taxOffice: optional(80),
  taxNumber: taxNumberInput,
  iban,
  paymentTerms: optional(200),
  notes: optional(4000),
};

export const createSupplierInput = z.object(supplierFields);
export const updateSupplierInput = z.object(supplierFields).partial().extend({ version });
export type CreateSupplierInput = z.input<typeof createSupplierInput>;

export const supplierListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  kind: z.enum(SUPPLIER_KINDS).optional(),
  archived: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const supplierOptionsQuery = z.object({
  kind: z.enum(SUPPLIER_KINDS),
  /** Another kind that also qualifies (visa handling sold as an extra). */
  also: z.enum(SUPPLIER_KINDS).optional(),
});

export const supplierServicesQuery = z.object({
  status: z.enum(['open', 'all']).default('open'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
